import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Anchor,
  MapPin, 
  User, 
  Phone,
  Clock,
  Car,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Navigation,
  RefreshCw,
  CreditCard,
  UserCheck,
  Timer,
  Receipt,
  XCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useNotifications } from "@/contexts/NotificationContext";
import { NotificationBell, PWAInstallPrompt } from "@/components/NotificationBell";
import { PushNotificationBanner, usePushNotifications } from "@/components/PushNotifications";
import ChatWidget from "@/components/ChatWidget";
import { useWhatsAppBubble } from "@/components/WhatsAppBubble";

import { API_URL } from "@/config/api";

// =============================================================================
// TRACKING STATES (as per specification)
// =============================================================================
// STATE_A: no_driver       - Driver not assigned
// STATE_B: driver_assigned - Driver assigned, not yet on the way
// STATE_C: tracking_active - Driver on the way / Trip active
// STATE_D: completed       - Trip completed
// =============================================================================

// Pay Online Button Component
const PayOnlineButton = ({ bookingId, amount }) => {
  const [loading, setLoading] = useState(false);

  const handlePayOnline = async () => {
    if (!bookingId) {
      toast.error("Booking information not available");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/create-checkout`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });
      
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Unable to create payment session");
      }
    } catch (error) {
      console.error("Payment error:", error);
      if (error.response?.status === 400) {
        toast.error("This booking has already been paid");
      } else {
        toast.error("Payment service unavailable. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handlePayOnline}
      disabled={loading}
      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3"
      data-testid="pay-online-btn"
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <CreditCard className="w-4 h-4 mr-2" />
          Pay Online Now (€{amount?.toFixed(2) || '0.00'})
        </>
      )}
    </Button>
  );
};

// Real-time map component showing driver location
const TrackingMap = ({ driverLocation, pickupAddress, terminalName }) => {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapInstanceRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const pickupMarkerRef = useRef(null);

  // Default to Le Havre port area
  const defaultCenter = { lat: 49.4847, lng: 0.1077 };

  // Load Leaflet CSS
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  // Create map instance
  useEffect(() => {
    if (mapLoaded && mapRef.current && window.L && !mapInstanceRef.current) {
      const center = driverLocation 
        ? [driverLocation.latitude, driverLocation.longitude]
        : [defaultCenter.lat, defaultCenter.lng];
      
      const map = window.L.map(mapRef.current).setView(center, 14);
      
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(map);

      mapInstanceRef.current = map;

      // Create custom icons
      const driverIcon = window.L.divIcon({
        html: `<div style="background: #f97316; border: 3px solid white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
            <circle cx="7" cy="17" r="2"/>
            <circle cx="17" cy="17" r="2"/>
          </svg>
        </div>`,
        className: 'driver-marker',
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });

      const pickupIcon = window.L.divIcon({
        html: `<div style="background: #10b981; border: 3px solid white; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>`,
        className: 'pickup-marker',
        iconSize: [36, 36],
        iconAnchor: [18, 36]
      });

      // Add driver marker if location available
      if (driverLocation) {
        driverMarkerRef.current = window.L.marker(
          [driverLocation.latitude, driverLocation.longitude],
          { icon: driverIcon }
        ).addTo(map).bindPopup('Your driver');
      }

      // Add pickup marker
      if (pickupAddress) {
        fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(pickupAddress)}&limit=1`)
          .then(res => res.json())
          .then(data => {
            if (data && data[0]) {
              const { lat, lon } = data[0];
              pickupMarkerRef.current = window.L.marker(
                [parseFloat(lat), parseFloat(lon)],
                { icon: pickupIcon }
              ).addTo(map).bindPopup(`Pickup Point`);

              if (driverLocation) {
                const bounds = window.L.latLngBounds([
                  [driverLocation.latitude, driverLocation.longitude],
                  [parseFloat(lat), parseFloat(lon)]
                ]);
                map.fitBounds(bounds, { padding: [50, 50] });
              }
            }
          })
          .catch(err => console.log('Geocoding error:', err));
      }
    }
  }, [mapLoaded, driverLocation, pickupAddress, terminalName]);

  // Update driver marker position
  useEffect(() => {
    if (mapInstanceRef.current && driverLocation && window.L) {
      const newLatLng = [driverLocation.latitude, driverLocation.longitude];
      
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setLatLng(newLatLng);
      } else {
        const driverIcon = window.L.divIcon({
          html: `<div style="background: #f97316; border: 3px solid white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
              <circle cx="7" cy="17" r="2"/>
              <circle cx="17" cy="17" r="2"/>
            </svg>
          </div>`,
          className: 'driver-marker',
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });
        driverMarkerRef.current = window.L.marker(newLatLng, { icon: driverIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup('Your driver');
      }

      mapInstanceRef.current.panTo(newLatLng, { animate: true, duration: 1 });
    }
  }, [driverLocation]);

  return (
    <div 
      ref={mapRef} 
      className="h-64 md:h-80 w-full rounded-lg border-2 border-slate-200 bg-slate-100"
      style={{ zIndex: 0, position: 'relative', minHeight: '256px' }}
      data-testid="tracking-map"
    />
  );
};

// Payment Status Badge Component
const PaymentStatusBadge = ({ status, amount }) => {
  const statusConfig = {
    pending: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      border: "border-amber-300",
      icon: Clock,
      label: "Payment Pending"
    },
    paid: {
      bg: "bg-green-100",
      text: "text-green-800",
      border: "border-green-300",
      icon: CheckCircle2,
      label: "Paid Online"
    },
    paid_by_cash: {
      bg: "bg-emerald-100",
      text: "text-emerald-800",
      border: "border-emerald-300",
      icon: CheckCircle2,
      label: "Paid (Cash)"
    },
    refunded: {
      bg: "bg-purple-100",
      text: "text-purple-800",
      border: "border-purple-300",
      icon: RefreshCw,
      label: "Refunded"
    }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 ${config.bg} ${config.text} border ${config.border} rounded-full text-sm font-medium`}>
      <Icon className="w-4 h-4" />
      <span>{config.label}</span>
      {amount > 0 && <span className="font-bold">€{amount.toFixed(2)}</span>}
    </div>
  );
};

export default function TrackingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [trackingData, setTrackingData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // States: 'loading', 'no_driver', 'driver_assigned', 'tracking_active', 'completed', 'error'
  const [trackingState, setTrackingState] = useState('loading');
  const [countdown, setCountdown] = useState(30);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [prevState, setPrevState] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const refreshIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const { setHideWhatsAppBubble } = useWhatsAppBubble();
  const { 
    notifications, 
    notifyDriverOnTheWay, 
    notifyDriverAssigned, 
    markAllAsRead,
    clearAllNotifications
  } = useNotifications();
  const { showLocalNotification, permission } = usePushNotifications();

  // Handle chat open state
  const handleChatOpenChange = useCallback((isOpen) => {
    setIsChatOpen(isOpen);
    setHideWhatsAppBubble(isOpen);
  }, [setHideWhatsAppBubble]);

  // Cleanup: show WhatsApp bubble when leaving page
  useEffect(() => {
    return () => {
      setHideWhatsAppBubble(false);
    };
  }, [setHideWhatsAppBubble]);

  // Determine tracking state based on data
  const determineTrackingState = useCallback((tracking, booking) => {
    // Priority 1: Check tracking session status
    if (tracking) {
      const sessionStatus = tracking.status;
      
      // STATE D: Trip completed (by driver or cancelled)
      if (sessionStatus === 'completed' || sessionStatus === 'cancelled' || sessionStatus === 'expired') {
        return 'completed';
      }
      
      // STATE D: Also check booking_status for completed
      if (tracking.booking_status === 'completed') {
        return 'completed';
      }
      
      // STATE C: Tracking active (driver on the way) - status must be 'active' AND have location
      if (sessionStatus === 'active' && tracking.current_location) {
        return 'tracking_active';
      }
      
      // STATE B_PAUSED: Tracking was paused (by driver or admin)
      if (sessionStatus === 'paused') {
        return 'tracking_paused';
      }
      
      // STATE B: Driver assigned, tracking session exists but:
      // - status is 'pending' (driver hasn't started GPS yet)
      // - status is 'active' but no location yet
      if (tracking.driver_name) {
        // Pending or active without location = driver assigned, waiting to start
        if (sessionStatus === 'pending' || (sessionStatus === 'active' && !tracking.current_location)) {
          return 'driver_assigned';
        }
        return 'driver_assigned';
      }
    }
    
    // Fallback to booking data (when no tracking session exists)
    if (booking) {
      // Check booking status for completed
      if (booking.status === 'completed') {
        return 'completed';
      }
      
      // STATE B: Driver assigned (in booking but no tracking session yet)
      if (booking.assigned_driver_id || booking.assigned_driver_name) {
        return 'driver_assigned';
      }
    }
    
    // STATE A: No driver assigned
    return 'no_driver';
  }, []);

  // Fetch tracking/booking data
  const fetchData = useCallback(async () => {
    if (!token) {
      setError("Invalid tracking link");
      setLoading(false);
      return;
    }

    try {
      // Try to get tracking session data
      const trackingResponse = await axios.get(`${API_URL}/api/tracking/view/${token}`);
      setTrackingData(trackingResponse.data);
      setLastRefresh(new Date());
      setCountdown(30);
      setError(null);
      
      // Determine state from tracking data
      const newState = determineTrackingState(trackingResponse.data, null);
      setTrackingState(newState);
      
    } catch (err) {
      console.log("Tracking fetch result:", err.response?.status);
      
      if (err.response?.status === 404) {
        // No tracking session - try to fetch booking directly
        try {
          const bookingResponse = await axios.get(`${API_URL}/api/bookings/${token}`);
          setBookingData(bookingResponse.data);
          setLastRefresh(new Date());
          setCountdown(30);
          setError(null);
          
          // Determine state from booking data
          const newState = determineTrackingState(null, bookingResponse.data);
          setTrackingState(newState);
          
        } catch (bookingErr) {
          if (bookingErr.response?.status === 404) {
            setError("This booking was not found. Please check your tracking link.");
          } else {
            setError("Unable to load booking information. Please try again later.");
          }
          setTrackingState('error');
        }
      } else if (err.response?.status === 410) {
        // Session expired or ended - this shouldn't happen with the new backend logic
        setError(err.response?.data?.detail || "This tracking session has ended.");
        setTrackingState('error');
      } else {
        // Keep last known state on temporary errors
        if (trackingData || bookingData) {
          // Don't change state on temporary network errors
          console.log("Temporary error, keeping last known state");
        } else {
          setError("Unable to load tracking information. Please try again.");
          setTrackingState('error');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [token, determineTrackingState, trackingData, bookingData]);

  // Initial fetch and auto-refresh management
  useEffect(() => {
    fetchData();

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);  // Only run once on mount

  // Manage auto-refresh based on state (stop for completed state)
  useEffect(() => {
    // Clear existing intervals
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Only auto-refresh for states A, B, C (not D - completed)
    if (trackingState !== 'completed' && trackingState !== 'error' && trackingState !== 'loading') {
      refreshIntervalRef.current = setInterval(fetchData, 30000);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => (prev > 0 ? prev - 1 : 30));
      }, 1000);
    }

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [trackingState, fetchData]);

  // Notifications on state changes
  useEffect(() => {
    if (trackingState !== prevState && prevState !== null) {
      const data = trackingData || bookingData;
      if (!data) return;

      // Driver just assigned
      if (trackingState === 'driver_assigned' && prevState === 'no_driver') {
        const driverName = trackingData?.driver_name || bookingData?.assigned_driver_name;
        
        notifyDriverAssigned({
          booking_id: data.booking_id,
          driver_name: driverName
        });
        
        if (permission === 'granted') {
          showLocalNotification('Driver Assigned!', {
            body: `${driverName || 'Your driver'} has been assigned to your booking`,
            tag: `driver-assigned-${data.booking_id}`,
            data: { url: `/track/${token}` }
          });
        }
      }

      // Tracking just became active
      if (trackingState === 'tracking_active' && prevState !== 'tracking_active') {
        const driverName = trackingData?.driver_name;
        
        notifyDriverOnTheWay({
          booking_id: data.booking_id,
          driver_name: driverName,
          tracking_token: token
        });
        
        if (permission === 'granted') {
          showLocalNotification('Driver On The Way!', {
            body: `${driverName || 'Your driver'} is heading to pick you up. Track live now!`,
            tag: `driver-on-way-${data.booking_id}`,
            requireInteraction: true,
            data: { url: `/track/${token}` }
          });
        }
      }

      // Trip completed notification
      if (trackingState === 'completed' && prevState !== 'completed') {
        if (permission === 'granted') {
          showLocalNotification('Trip Completed', {
            body: 'Your trip has been completed. View your receipt.',
            tag: `trip-completed-${data?.booking_id}`,
            data: { url: `/client/booking/${data?.booking_id}` }
          });
        }
      }
    }
    setPrevState(trackingState);
  }, [trackingState, prevState, trackingData, bookingData, token, notifyDriverAssigned, notifyDriverOnTheWay, permission, showLocalNotification]);

  // Manual refresh
  const handleRefresh = () => {
    setCountdown(30);
    fetchData();
  };

  // Navigate to receipt
  const handleViewReceipt = () => {
    const booking = trackingData?.booking_id || bookingData?.booking_id;
    if (booking) {
      navigate(`/client/booking/${booking}`);
    }
  };

  // Get display data
  const displayData = trackingData || bookingData;
  const driverName = trackingData?.driver_name || bookingData?.assigned_driver_name;
  const passengerName = trackingData?.passenger_name || bookingData?.passenger_name;
  const terminalName = trackingData?.terminal_name || bookingData?.terminal_name;
  const pickupAddress = trackingData?.pickup_address || bookingData?.pickup_address;
  const pickupDatetime = trackingData?.pickup_datetime || bookingData?.pickup_datetime;
  const paymentStatus = trackingData?.payment_status || bookingData?.payment_status || 'pending';
  const finalPrice = trackingData?.final_price || bookingData?.final_price || 0;
  const bookingId = trackingData?.booking_id || bookingData?.booking_id || token;
  
  // Vehicle info
  const vehicleBrand = trackingData?.driver_vehicle_brand || bookingData?.driver_vehicle_brand;
  const vehicleModel = trackingData?.driver_vehicle_model || bookingData?.driver_vehicle_model;
  const licensePlate = trackingData?.driver_license_plate || bookingData?.driver_license_plate;
  const hasVehicleInfo = vehicleBrand || vehicleModel || licensePlate;

  // Format pickup time
  const formatPickupTime = () => {
    if (!pickupDatetime) return "N/A";
    try {
      const dt = new Date(pickupDatetime);
      return dt.toLocaleString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return pickupDatetime;
    }
  };

  // Format time since last update
  const getTimeSinceUpdate = () => {
    if (!trackingData?.last_updated) return null;
    const lastUpdate = new Date(trackingData.last_updated);
    const now = new Date();
    const diffSeconds = Math.floor((now - lastUpdate) / 1000);
    
    if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} minutes ago`;
    return `${Math.floor(diffSeconds / 3600)} hours ago`;
  };

  // Get payment status text for completed state
  const getPaymentStatusText = () => {
    if (paymentStatus === 'paid') return 'Paid Online';
    if (paymentStatus === 'paid_by_cash') return 'Paid by Cash';
    return 'Pending';
  };

  // Loading state
  if (loading && !displayData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && trackingState === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Tracking Unavailable</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-orange-500 hover:bg-orange-600">
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black text-white tracking-tight">Y GO</span>
            </Link>
            <div className="flex items-center gap-3">
              {/* Auto-refresh indicator - only show for active states */}
              {trackingState !== 'completed' && trackingState !== 'error' && (
                <div className="hidden sm:flex items-center gap-2 text-slate-400 text-xs">
                  <Timer className="w-3 h-3" />
                  <span>Refresh in {countdown}s</span>
                </div>
              )}
              {/* Notification Bell */}
              <NotificationBell 
                notifications={notifications}
                onClear={clearAllNotifications}
                onMarkAllRead={markAllAsRead}
              />
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Navigation className="w-4 h-4" />
                <span className="hidden sm:inline">Live Tracking</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Push Notification Permission Banner */}
      <PushNotificationBanner />

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        
        {/* ================================================================= */}
        {/* STATE A: No Driver Assigned Yet */}
        {/* ================================================================= */}
        {trackingState === 'no_driver' && (
          <>
            <div className="mb-6 p-6 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <Clock className="w-8 h-8 animate-pulse" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Driver Assignment Pending</h1>
                  <p className="text-amber-100 text-sm mt-1">
                    Driver assignment pending, please wait.
                  </p>
                </div>
              </div>
            </div>

            <Card className="shadow-lg" data-testid="state-a-no-driver">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Your Booking</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRefresh}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Check Status
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status Message */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Driver assignment pending, please wait.</p>
                      <p className="text-sm text-amber-600 mt-1">
                        This page auto-updates every 30 seconds. You'll be notified when a driver is assigned.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pickup Details */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pickup Location</p>
                    <p className="font-medium text-slate-900">{terminalName}</p>
                    {pickupAddress && <p className="text-sm text-slate-600">{pickupAddress}</p>}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Scheduled Pickup</p>
                    <p className="font-medium text-slate-900">{formatPickupTime()}</p>
                  </div>
                </div>

                {/* Payment Status */}
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Payment Status</span>
                    <PaymentStatusBadge status={paymentStatus} amount={finalPrice} />
                  </div>
                </div>
                
                {/* Booking Reference */}
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 text-center">
                    Booking Reference: <span className="font-mono font-bold text-slate-700">{bookingId}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ================================================================= */}
        {/* STATE B: Driver Assigned, Not Yet On The Way */}
        {/* ================================================================= */}
        {trackingState === 'driver_assigned' && (
          <>
            <div className="mb-6 p-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <UserCheck className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Driver Assigned!</h1>
                  <p className="text-blue-100 text-sm mt-1">
                    Driver assigned. Tracking will start when the driver is on the way.
                  </p>
                </div>
              </div>
            </div>

            <Card className="shadow-lg" data-testid="state-b-driver-assigned">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Trip Details</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRefresh}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status Message */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Car className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Driver assigned. Tracking will start when the driver is on the way.</p>
                      <p className="text-sm text-blue-600 mt-1">
                        This page auto-updates every 30 seconds.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Driver Info */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{driverName || "Driver Assigned"}</p>
                    <p className="text-sm text-slate-500">Your Driver</p>
                  </div>
                </div>

                {/* Vehicle Info */}
                {hasVehicleInfo && (
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Car className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">
                        {vehicleBrand && vehicleModel 
                          ? `${vehicleBrand} ${vehicleModel}` 
                          : vehicleBrand || vehicleModel || "Vehicle"}
                      </p>
                      {licensePlate && (
                        <p className="text-sm font-mono font-bold text-orange-600 bg-orange-100 inline-block px-2 py-0.5 rounded mt-1">
                          {licensePlate}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Pickup Details */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pickup Location</p>
                    <p className="font-medium text-slate-900">{terminalName}</p>
                    {pickupAddress && <p className="text-sm text-slate-600">{pickupAddress}</p>}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Scheduled Pickup</p>
                    <p className="font-medium text-slate-900">{formatPickupTime()}</p>
                  </div>
                </div>

                {/* Payment Status */}
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Payment Status</span>
                    <PaymentStatusBadge status={paymentStatus} amount={finalPrice} />
                  </div>
                </div>

                {/* Booking Reference */}
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 text-center">
                    Booking Reference: <span className="font-mono font-bold text-slate-700">{bookingId}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ================================================================= */}
        {/* STATE B_PAUSED: Tracking Paused */}
        {/* ================================================================= */}
        {trackingState === 'tracking_paused' && (
          <>
            <div className="mb-6 p-6 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <Clock className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Tracking Paused</h1>
                  <p className="text-amber-100 text-sm mt-1">
                    Your driver has temporarily paused GPS sharing.
                  </p>
                </div>
              </div>
            </div>

            <Card className="shadow-lg" data-testid="state-b-paused">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Trip Details</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRefresh}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status Message */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">GPS tracking temporarily paused.</p>
                      <p className="text-sm text-amber-600 mt-1">
                        Your driver will resume tracking shortly. This page auto-updates every 30 seconds.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Driver Info */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{driverName || "Driver Assigned"}</p>
                    <p className="text-sm text-slate-500">Your Driver</p>
                  </div>
                </div>

                {/* Vehicle Info */}
                {hasVehicleInfo && (
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Car className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">
                        {vehicleBrand && vehicleModel 
                          ? `${vehicleBrand} ${vehicleModel}` 
                          : vehicleBrand || vehicleModel || "Vehicle"}
                      </p>
                      {licensePlate && (
                        <p className="text-sm font-mono font-bold text-orange-600 bg-orange-100 inline-block px-2 py-0.5 rounded mt-1">
                          {licensePlate}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Pickup Details */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pickup Location</p>
                    <p className="font-medium text-slate-900">{terminalName}</p>
                    {pickupAddress && <p className="text-sm text-slate-600">{pickupAddress}</p>}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Scheduled Pickup</p>
                    <p className="font-medium text-slate-900">{formatPickupTime()}</p>
                  </div>
                </div>

                {/* Payment Status */}
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Payment Status</span>
                    <PaymentStatusBadge status={paymentStatus} amount={finalPrice} />
                  </div>
                </div>

                {/* Booking Reference */}
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 text-center">
                    Booking Reference: <span className="font-mono font-bold text-slate-700">{bookingId}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ================================================================= */}
        {/* STATE C: Tracking Active - Driver On The Way */}
        {/* ================================================================= */}
        {trackingState === 'tracking_active' && trackingData && (
          <>
            <div className="mb-6 p-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl text-white shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                  <Car className="w-7 h-7" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Your driver is on the way!</h1>
                  <p className="text-orange-100 text-sm">
                    {trackingData?.driver_name} is heading to pick you up
                  </p>
                </div>
              </div>
            </div>

            {/* Map Card - ONLY shown in STATE C */}
            <Card className="mb-4 overflow-hidden shadow-xl" data-testid="state-c-tracking-active">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    Live Location
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRefresh}
                    disabled={loading}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                {trackingData?.last_updated && (
                  <p className="text-xs text-slate-500">
                    Last updated: {getTimeSinceUpdate()}
                  </p>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <TrackingMap 
                  driverLocation={trackingData?.current_location}
                  pickupAddress={trackingData?.pickup_address}
                  terminalName={trackingData?.terminal_name}
                />
              </CardContent>
            </Card>

            {/* Booking Details */}
            <Card className="shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Pickup Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Driver Info */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{trackingData?.driver_name}</p>
                    <p className="text-sm text-slate-500">Your Driver</p>
                  </div>
                </div>

                {/* Vehicle Info */}
                {hasVehicleInfo && (
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Car className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">
                        {vehicleBrand && vehicleModel 
                          ? `${vehicleBrand} ${vehicleModel}` 
                          : vehicleBrand || vehicleModel || "Vehicle"}
                      </p>
                      {licensePlate && (
                        <p className="text-sm font-mono font-bold text-orange-600 bg-orange-100 inline-block px-2 py-0.5 rounded mt-1">
                          {licensePlate}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Pickup Location */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pickup Location</p>
                    <p className="font-medium text-slate-900">{trackingData?.terminal_name}</p>
                    <p className="text-sm text-slate-600">{trackingData?.pickup_address}</p>
                  </div>
                </div>

                {/* Pickup Time */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Scheduled Pickup</p>
                    <p className="font-medium text-slate-900">{formatPickupTime()}</p>
                  </div>
                </div>

                {/* Passenger */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Passenger</p>
                    <p className="font-medium text-slate-900">{trackingData?.passenger_name}</p>
                  </div>
                </div>

                {/* Booking Reference */}
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 text-center">
                    Booking Reference: <span className="font-mono font-bold">{trackingData?.booking_id}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ================================================================= */}
        {/* STATE D: Trip Completed */}
        {/* ================================================================= */}
        {trackingState === 'completed' && (
          <>
            <div className="mb-6 p-6 bg-gradient-to-r from-green-500 to-green-600 rounded-xl text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Trip Completed</h1>
                  <p className="text-green-100 text-sm mt-1">
                    Thank you for traveling with Y GO!
                  </p>
                </div>
              </div>
            </div>

            <Card className="shadow-lg" data-testid="state-d-completed">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Trip Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Completion Message */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Trip completed.</p>
                      <p className="text-sm text-green-600 mt-1">
                        Your journey has been successfully completed.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Driver Info */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{driverName || "Your Driver"}</p>
                    <p className="text-sm text-slate-500">Driver</p>
                  </div>
                </div>

                {/* Pickup Details */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pickup Location</p>
                    <p className="font-medium text-slate-900">{terminalName}</p>
                    {pickupAddress && <p className="text-sm text-slate-600">{pickupAddress}</p>}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pickup Time</p>
                    <p className="font-medium text-slate-900">{formatPickupTime()}</p>
                  </div>
                </div>

                {/* Final Payment Status - CRITICAL for STATE D */}
                <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Final Payment Status</span>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                      paymentStatus === 'paid' || paymentStatus === 'paid_by_cash' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {paymentStatus === 'paid' || paymentStatus === 'paid_by_cash' ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                      {getPaymentStatusText()}
                    </div>
                  </div>
                  <div className="mt-2 text-right">
                    <span className="text-2xl font-bold text-slate-900">€{finalPrice?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>

                {/* Booking Reference */}
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 text-center">
                    Booking Reference: <span className="font-mono font-bold text-slate-700">{bookingId}</span>
                  </p>
                </div>

                {/* CTA: View Receipt */}
                <div className="pt-4">
                  <Button 
                    onClick={handleViewReceipt}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3"
                    data-testid="view-receipt-btn"
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    View Your Receipt
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Payment Section - Show for states A, B, C if payment pending */}
        {trackingState !== 'completed' && displayData && paymentStatus !== "paid" && paymentStatus !== "paid_by_cash" && (
          <Card className="mt-4 shadow-lg border-amber-300 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-800">Payment Required</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Amount due: <span className="font-bold">€{finalPrice?.toFixed(2) || '0.00'}</span>
                  </p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-amber-200 space-y-3">
                <PayOnlineButton 
                  bookingId={bookingId} 
                  amount={finalPrice}
                />
                
                <div className="text-center">
                  <p className="text-xs text-amber-600 mb-2">— OR —</p>
                  <p className="text-sm text-amber-700">
                    Pay <span className="font-bold">€{finalPrice?.toFixed(2) || '0.00'}</span> in cash to the driver
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Confirmed - Show for states A, B, C if already paid */}
        {trackingState !== 'completed' && displayData && (paymentStatus === "paid" || paymentStatus === "paid_by_cash") && (
          <Card className="mt-4 shadow-lg border-green-300 bg-green-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Payment Confirmed</p>
                  <p className="text-sm text-green-700">
                    {paymentStatus === "paid" ? "Paid Online" : "Paid by Cash"} • €{finalPrice?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <div className="mt-6 text-center">
          <p className="text-slate-400 text-sm mb-2">Need help?</p>
          <a 
            href="tel:+33278780404" 
            className="inline-flex items-center gap-2 text-white bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg transition-colors"
          >
            <Phone className="w-4 h-4" />
            +33 2 78 78 04 04
          </a>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-xs">
          <p>Y GO - Seamen Shore Services</p>
          <p>Le Havre, France</p>
        </div>
      </div>

      {/* Chat Widget - Only show when driver is assigned AND trip not completed */}
      {(trackingState === 'driver_assigned' || trackingState === 'tracking_active') && bookingId && (
        <ChatWidget 
          bookingId={bookingId}
          userType="client"
          userName={passengerName}
          driverName={driverName}
          autoOpenOnMessage={true}
          onOpenChange={handleChatOpenChange}
        />
      )}
    </div>
  );
}
